import { ListItemIcon, MenuItem } from '@mui/material';
import { Permission } from '@procureai/permissions';
import { PermissionCheck, usePermissions } from '@procureai/permissions-ui';
import { PageTitleWithMenu } from '@procureai/ui-mui';
import { ReactNode } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { DomainIcon } from '../DomainIcon/DomainIcon';
import { NegotiationDomain, NegotiationDomainType } from '~/common/types';
import { RouteId } from '~/routes';
import { mapTruthy } from '~/util/array';

type DomainSelectItem = { id: NegotiationDomain; neededPermission?: PermissionCheck };

const domainSelectItems: DomainSelectItem[] = [
  {
    id: NegotiationDomainType.DEMAND,
    neededPermission: Permission.DemandList,
  },
  {
    id: NegotiationDomainType.NEGOTIATION_CASE,
    neededPermission: Permission.CaseList,
  },
];

export interface NegotiationPageTitleProps {
  domain?: NegotiationDomain;
}

const routeIdMap: { [key in NegotiationDomainType]?: string } = {
  [NegotiationDomainType.NEGOTIATION_CASE]: RouteId.negotiationCases,
  [NegotiationDomainType.DEMAND]: RouteId.demand,
};

export const NegotiationPageTitle = ({ domain }: NegotiationPageTitleProps) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();

  const menuItems = mapTruthy<DomainSelectItem, ReactNode>(({ id, neededPermission }) => {
    const selected = id === domain;
    if (!neededPermission || hasPermission(neededPermission)) {
      return (
        <MenuItem key={id} component={RouterLink} to={routeIdMap[id] ?? RouteId.demand} selected={selected}>
          <ListItemIcon>
            <DomainIcon domain={id} />
          </ListItemIcon>
          <span>{t(`Negotiation.${id}.title`)}</span>
        </MenuItem>
      );
    }
    return null;
  })(domainSelectItems);

  const title = t(`Negotiation.${domain}.title`);

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <PageTitleWithMenu title={title} disabled={menuItems.length <= 1}>
        {menuItems}
      </PageTitleWithMenu>
    </>
  );
};
